import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

import { apipost, bizpost } from '../../../ajax';
import { BaseCard } from '../../../components/wizard';
import SendPanel from './SendPanel';
import { normalizePhonePayload } from '../../../core/phone';

interface UploadResult {
  success: boolean;
  url?: string;
  publicId?: string;
  error?: string;
}

class SendCard extends Component<any, any> {
  static propTypes: any;
  static defaultProps: any;

  constructor(props: any) {
    super(props);
    this.state = { loading: false };
  }

  startLoading = () => {
    this.setState({ loading: true });
  };

  stopLoading = () => {
    this.setState({ loading: false });
  };

  //get member photo name
  generateMemberPhotoName = (memberId: string | number, originalName: string): string => {
    const timestamp = Date.now();
    const fileExtension = originalName.split('.').pop();
    return `member_${memberId}_${timestamp}.${fileExtension}`;
  };

  uploadPhoto = async (
    file: File,
    folder: string = 'members',
    fileName?: string
  ): Promise<UploadResult> => {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        return {
          success: false,
          error: 'Please select a valid image file'
        };
      }

      // Validate file size (max 10MB for Cloudinary)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        return {
          success: false,
          error: 'File size must be less than 10MB'
        };
      }

      // Generate filename if not provided
      const timestamp = Date.now();
      const finalFileName = fileName || `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'cms-upload');
      formData.append('folder', folder);
      formData.append('public_id', finalFileName);
      formData.append('quality', 'auto');
      formData.append('fetch_format', 'auto');

      // Upload to Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/djp5oy3ri/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();

      return {
        success: true,
        url: result.secure_url,
        publicId: result.public_id
      };
    } catch (error) {
      console.error('Error uploading photo:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload photo'
      };
    }
  };

  doSubmit = async () => {
    const { code, service, sconfig, showNotification, record, data, ft, ms, mt } = this.props;
    const { url, endpoint } = sconfig || {};
    const formdata = (data && data.data) || {};
    const memberPhoto = data?.photo;
    let response: any;
    try {
      this.startLoading();

      // get public url to be used in place of 'img'
      let imageUrl = formdata.img || record.img || '';
      const hasPhotoFile = memberPhoto?.file instanceof File;
      if (hasPhotoFile) {
        const memberRef = record?.rid || formdata?.rid || formdata?.con || `${formdata?.fnm || 'member'}_${formdata?.lnm || ''}`;
        const photoName = this.generateMemberPhotoName(memberRef, memberPhoto.file.name);
        const upload = await this.uploadPhoto(memberPhoto.file, 'members', photoName);
        if (!upload.success || !upload.url) {
          showNotification(upload.error || 'Photo upload failed', 'error', 'Upload Error');
          return Promise.reject(upload);
        }
        imageUrl = upload.url;
      }

      // Optional photo: only persist img when it's a real URL.
      if (!(typeof imageUrl === 'string' && /^https?:\/\//.test(imageUrl))) {
        imageUrl = '';
      }

      // submit wizard with payloads
      const payload = normalizePhonePayload({
        ...record,
        ...formdata,
        img: imageUrl,
        s: service,
        a: 'wizard',
        ft,
        ms,
        mt,
      });
      if (endpoint === 'api') response = await apipost(url, code, payload);
      else response = await bizpost(payload);

      if (response?.success) {
        showNotification(response.sm, 'success', response.st);
        return response;
      }

      // Optional fallback for deployments where wizard action is not yet exposed.
      if (response?.failure) {
        const fallback = normalizePhonePayload({ ...payload, a: 'add' });
        if (endpoint === 'api') response = await apipost(url, code, fallback);
        else response = await bizpost(fallback);
        if (response?.success) {
          showNotification(response.sm, 'success', response.st);
          return response;
        }
      }

      showNotification(
        response?.message || 'Unable to submit member data',
        response?.type || 'error',
        response?.title || 'Submission Error'
      );
      return Promise.reject(response);
    } catch (err) {
      const error: any = err || {};
      showNotification(
        error.message || 'request failed',
        error.type || 'warn',
        error.title || 'Update Error'
      );
      return Promise.reject(error);
    } finally {
      this.stopLoading();
    }
  };

  render = () => {
    const { record = {}, data = {}, extras, setItem, ...extraProps } = this.props;
    const { loading } = this.state;
    const formdata = data.data || {};
    return (
      <Box>
        <BaseCard
          cardid={'SEND'}
          cardname={'Send'}
          cardtitle={'Review & Submit'}
          instruction={'Review all information and submit to create the member record.'}
          step={6}
          buttons={['START', 'CANCEL', 'PREV', 'SEND', 'GUIDE']}
          doSubmit={this.doSubmit.bind(this)}
          {...extraProps}
        >
          <SendPanel
            data={data}
            record={{ ...record, ...formdata }}
            extras={extras}
            loading={loading}
            setItem={setItem}
          />
        </BaseCard>
      </Box>
    );
  };
}

SendCard.propTypes = {
  classes: PropTypes.object,
};

export default SendCard;
